import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Brain, X, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { nexaApi } from './api';

export const MemoryView = () => {
  const [memories, setMemories] = useState(null);
  const [docs, setDocs] = useState([]);
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');

  useEffect(() => {
    nexaApi.get('/memories').then(({ data }) => setMemories(data.memories));
    nexaApi.get('/docs').then(({ data }) => setDocs(data.docs)).catch(() => {});
  }, []);

  const removeDoc = async (d) => {
    setDocs((prev) => prev.filter((x) => x.id !== d.id));
    try {
      await nexaApi.delete(`/docs/${d.id}`);
      toast.success('Document forgotten');
    } catch {
      toast.error('Could not delete');
    }
  };

  const remove = async (m) => {
    setMemories((prev) => prev.filter((x) => x.id !== m.id));
    try {
      await nexaApi.delete(`/memories/${m.id}`);
      toast.success('Memory forgotten');
    } catch {
      toast.error('Could not delete');
      nexaApi.get('/memories').then(({ data }) => setMemories(data.memories));
    }
  };

  if (!memories) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 border-2 border-sky-400/30 border-t-sky-400 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4" data-testid="nexa-memory-view">
      <p className="text-white/40 text-sm -mb-2">
        What NEXA has learned about you. She updates this after every conversation.
      </p>

      {docs.length > 0 && (
        <div className="rounded-3xl border border-white/10 bg-[#0A0A0A] overflow-hidden" data-testid="nexa-docs-list">
          <p className="px-5 pt-4 pb-1 text-[11px] font-mono tracking-[0.25em] uppercase text-sky-400/70">
            Documents
          </p>
          {docs.map((d, idx) => (
            <div key={d.id} data-testid={`nexa-doc-item-${idx}`}
              className={`px-5 py-3.5 flex items-center gap-3 ${idx < docs.length - 1 ? 'border-b border-white/[0.06]' : ''}`}>
              <FileText className="w-4 h-4 text-sky-400 shrink-0" strokeWidth={1.5} />
              <div className="flex-1 min-w-0">
                <p className="text-[14px] text-white/90 truncate">{d.filename}</p>
                <p className="text-[10px] text-white/30 mt-0.5">
                  {d.chunks} sections · {new Date(d.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                </p>
              </div>
              <button type="button" onClick={() => removeDoc(d)} aria-label="Forget document"
                data-testid={`nexa-doc-delete-${idx}`}
                className="w-8 h-8 rounded-full border border-white/10 flex items-center justify-center text-white/40 hover:text-white/80 shrink-0">
                <X className="w-3.5 h-3.5" strokeWidth={2} />
              </button>
            </div>
          ))}
        </div>
      )}

      {memories.length > 0 && (
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search memories…"
          data-testid="nexa-memory-search"
          className="nexa-input w-full h-12 rounded-2xl px-5 text-sm"
        />
      )}

      {memories.length > 0 && (
        <div className="flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-testid="nexa-memory-filters">
          {['all', ...Array.from(new Set(memories.map((m) => m.category || 'other')))].map((c) => (
            <button
              key={c}
              onClick={() => setFilter(c)}
              data-testid={`nexa-memory-filter-${c}`}
              className={`shrink-0 px-4 h-9 rounded-full text-xs font-semibold capitalize border transition-colors ${
                filter === c ? 'bg-sky-400 text-black border-sky-400' : 'border-white/15 text-white/60'
              }`}
            >
              {c}
              <span className="ml-1 opacity-60">
                {c === 'all' ? memories.length : memories.filter((m) => (m.category || 'other') === c).length}
              </span>
            </button>
          ))}
        </div>
      )}

      {memories.length === 0 ? (
        <div className="rounded-3xl border border-white/10 bg-[#0A0A0A] p-8 flex flex-col items-center text-center">
          <Brain className="w-8 h-8 text-sky-400 mb-3" strokeWidth={1.3} />
          <p className="text-sm text-white/60">
            Nothing learned yet. Talk to NEXA — tell her about your goals, preferences and plans,
            and they'll appear here.
          </p>
        </div>
      ) : (
        <div className="rounded-3xl border border-white/10 bg-[#0A0A0A] overflow-hidden" data-testid="nexa-memory-list">
          <AnimatePresence initial={false}>
            {memories
              .filter((m) => filter === 'all' || (m.category || 'other') === filter)
              .filter((m) => !query.trim() || m.content.toLowerCase().includes(query.trim().toLowerCase()))
              .map((m, idx) => (
              <motion.div
                key={m.id}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
                data-testid={`nexa-memory-item-${idx}`}
                className={`px-5 py-4 flex items-start gap-3 ${
                  idx < memories.length - 1 ? 'border-b border-white/[0.06]' : ''
                }`}
              >
                <Brain className="w-4 h-4 text-sky-400 mt-0.5 shrink-0" strokeWidth={1.5} />
                <div className="flex-1 min-w-0">
                  <p className="text-[15px] leading-snug text-white/90">{m.content}</p>
                  <p className="text-[10px] text-white/30 mt-1 tracking-wide">
                    Learned{' '}
                    {new Date(m.created_at).toLocaleDateString('en-GB', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                    })}
                    {m.updated_at !== m.created_at ? ' · updated' : ''}
                  </p>
                </div>
                <motion.button
                  whileTap={{ scale: 0.85 }}
                  onClick={() => remove(m)}
                  data-testid={`nexa-memory-delete-${idx}`}
                  aria-label="Forget this memory"
                  className="w-8 h-8 rounded-full border border-white/10 flex items-center justify-center text-white/40 hover:text-white/80 shrink-0"
                >
                  <X className="w-3.5 h-3.5" strokeWidth={2} />
                </motion.button>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}

      <p className="text-xs text-white/30 text-center px-6">
        Deleting a memory means NEXA forgets it permanently.
      </p>
    </div>
  );
};
